import { useGridFilter } from '@mint-lib/ag-grid/react';
import { useCallback, useState } from 'react';

import type { GridApi } from '@mint-lib/ag-grid/community';
import {
  CalendarRange,
  type DateRange,
} from '@mint-lib/tw-components/patterns';
import { format } from 'date-fns';

const doesFilterPass = () => true;

export type DateRangeModel = {
  filterType: 'dateRange';
  startDate: string;
  endDate: string;
};

type GridDateRangePickerProps = {
  api: GridApi;
  model: null | DateRangeModel;
  onModelChange: (model: null | DateRangeModel) => void;
};

export const GridDateRangePicker = (props: GridDateRangePickerProps) => (
  <GridDateRangePickerView
    key={`${props.model?.startDate}-${props.model?.endDate}`}
    {...props}
  />
);

export const GridDateRangePickerView = ({
  api,
  model,
  onModelChange,
}: GridDateRangePickerProps) => {
  useGridFilter({
    doesFilterPass,
  });

  const [selectedRange, setSelectedRange] = useState<DateRange>({
    from: model?.startDate ? new Date(model.startDate) : undefined,
    to: model?.endDate ? new Date(model.endDate) : undefined,
  });

  const [selectedMonth, setSelectedMonth] = useState<Date>(
    model?.startDate ? new Date(model.startDate) : new Date(),
  );

  const handleDateApply = useCallback(() => {
    if (!selectedRange.from || !selectedRange.to) {
      return;
    }

    const model = {
      field: 'importDate',
      filterType: 'dateRange' as const,
      startDate: format(selectedRange.from, 'yyyy-MM-dd'),
      endDate: format(selectedRange.to, 'yyyy-MM-dd'),
    };
    onModelChange(model);
    api.hideColumnFilter();
  }, [api, onModelChange, selectedRange]);

  const handleReset = useCallback(() => {
    const emptyRange = { from: undefined, to: undefined };
    setSelectedRange(emptyRange);
    onModelChange(null);
  }, [onModelChange]);

  const handleRangeSelect = useCallback((range?: DateRange) => {
    if (range) {
      setSelectedRange(range);
    }
  }, []);

  const handleMonthChange = useCallback((month: Date) => {
    setSelectedMonth(month);
  }, []);

  return (
    <div className="min-h-[340px] min-w-[505px]">
      <CalendarRange
        month={selectedMonth}
        onMonthChange={handleMonthChange}
        selected={selectedRange}
        onReset={handleReset}
        onSelect={handleRangeSelect}
        onApply={handleDateApply}
      />
    </div>
  );
};
